import React, { useState } from "react";
import { Button, Checkbox, Form, Input, Select } from "antd";
import { TUser } from "../../types/config";
import EncryptUtils from "../../utils/encrypt";
import useFetch from "../../hooks/useFetch";
import useToast from "../../hooks/useToast";
import { eResultCode } from "../../utils/enum";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import { useNavigate } from "react-router-dom";
import { CloseOutlined, CheckOutlined } from "@ant-design/icons";
import styles from "./auth.module.css";

const { Option } = Select;

const formItemLayout = {
  labelCol: {
    span: 24, // Ensures label spans the full width above the input
  },
  wrapperCol: {
    span: 24, // Ensures input also spans the full width below the label
  },
};

const Register: React.FC = () => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Hook for making API requests
  const { post } = useFetch();
  // Hook for displaying toasts
  const { onShowToast } = useToast();
  const navigate = useNavigate();

  const onFinish = async (values: TUser) => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          displayname: values.userName,
          fullName: values.userName,
          emailId: values.emailId,
          password: EncryptUtils.encrypt(values.password as string),
          mobileNumber: values.mobileNumber,
          roleId: 1,
          id: 0,
          roles: [],
        },
      };
      const response = await post("/signUp", payload);
      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode == eResultCode.SUCCESS) {
        setIsLoading(false);
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        navigate("/login");
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const prefixSelector = (
    <Form.Item name="prefix" noStyle>
      <Select style={{ width: 70 }}>
        <Option value="86">+91</Option>
        {/* <Option value="87">+87</Option> */}
      </Select>
    </Form.Item>
  );

  const [autoCompleteResult, setAutoCompleteResult] = useState<string[]>([]);

  return (
    <div className={styles.parentWrapper}>
    <Form
      {...formItemLayout}
      form={form}
      name="register"
      onFinish={onFinish}
      initialValues={{}}
      style={{ maxWidth: 600 }}
      scrollToFirstError
      layout="vertical" // This is the key setting to make labels appear on top
    >
      <Form.Item
        name="emailId"
        label="E-mail"
        rules={[
          {
            type: "email",
            message: "The input is not valid E-mail!",
          },
          {
            required: true,
            message: "Please input your E-mail!",
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="password"
        label="Password"
        rules={[
          {
            required: true,
            message: "Please input your password!",
          },
          {
            pattern:
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            message:
              "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
          },
        ]}
        hasFeedback
      >
        <Input.Password />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label="Confirm Password"
        dependencies={["password"]}
        hasFeedback
        rules={[
          {
            required: true,
            message: "Please confirm your password!",
          },
          {
            pattern:
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            message:
              "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error("The new password that you entered do not match!")
              );
            },
          }),
        ]}
      >
        <Input.Password />
      </Form.Item>

      <Form.Item
        name="userName"
        label="Username"
        tooltip="What do you want others to call you?"
        rules={[
          {
            required: true,
            message: "Please input your nickname!",
            whitespace: true,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="mobileNumber"
        label="Phone Number"
        rules={[
          { required: true, message: "Please input your phone number!" },
          {
            pattern: /^\d{10}$/,
            message: "Phone number must be exactly 10 digits!",
          },
        ]}
      >
        <Input
          addonBefore={prefixSelector}
          maxLength={10}
          style={{ width: "100%" }}
        />
      </Form.Item>

      <Form.Item
        name="agreement"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject(new Error("Should accept agreement")),
          },
        ]}
      >
        <Checkbox>
          I have read the <a href="">agreement</a>
        </Checkbox>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" disabled={isLoading}>
          Register
        </Button>
      </Form.Item>
    </Form>
  </div>
  );
};

export default Register;
